import {type QueryClient, useQuery} from '@tanstack/react-query'

import {
  aggregateUserInterests,
  createBskyTopicsHeader,
} from '#/lib/api/feed/utils'
import {logger} from '#/logger'
import {getContentLanguages} from '#/state/preferences/languages'
import {STALE} from '#/state/queries'
import {usePreferencesQuery} from '#/state/queries/preferences'
import {useAppviewClient} from '#/state/session'
import {app} from '#/lexicons'
import {canFallbackToGeneralSuggestions} from './suggested-users-fallback'

export type QueryProps = {
  category?: string | null
  limit?: number
}

export const getSuggestedUsersForExploreQueryKeyRoot =
  'unspecced-suggested-users-for-explore'
export const createGetSuggestedUsersForExploreQueryKey = (
  props: QueryProps,
) => [getSuggestedUsersForExploreQueryKeyRoot, props.category, props.limit]

export function useGetSuggestedUsersForExploreQuery(props: QueryProps = {}) {
  const client = useAppviewClient()
  const {data: preferences} = usePreferencesQuery()

  return useQuery({
    staleTime: STALE.MINUTES.THREE,
    queryKey: createGetSuggestedUsersForExploreQueryKey(props),
    queryFn: async () => {
      const contentLangs = getContentLanguages().join(',')
      const userInterests = aggregateUserInterests(preferences)

      const data = await client
        .call(
          app.bsky.unspecced.getSuggestedUsersForExplore,
          {
            category: props.category ?? undefined,
            limit: props.limit || 10,
          },
          {
            headers: {
              ...createBskyTopicsHeader(userInterests),
              'Accept-Language': contentLangs,
            },
          },
        )
        .catch(async error => {
          if (!canFallbackToGeneralSuggestions(error)) throw error
          const suggestions = await client.call(
            app.bsky.actor.getSuggestions,
            {limit: props.limit || 10},
            {headers: {'Accept-Language': contentLangs}},
          )
          return {actors: suggestions.actors, recIdStr: undefined}
        })

      if (!data.recIdStr) {
        logger.debug('getSuggestedUsersForExplore response missing recIdStr')
      }
      return {...data, recId: data.recIdStr}
    },
  })
}

export function* findAllProfilesInQueryData(
  queryClient: QueryClient,
  did: string,
): Generator<app.bsky.actor.defs.ProfileView, void> {
  const responses =
    queryClient.getQueriesData<app.bsky.unspecced.getSuggestedUsersForExplore.$OutputBody>(
      {
        queryKey: [getSuggestedUsersForExploreQueryKeyRoot],
      },
    )
  for (const [_key, response] of responses) {
    if (!response) {
      continue
    }

    for (const actor of response.actors) {
      if (actor.did === did) {
        yield actor
      }
    }
  }
}
